import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  IconButton,
  Table,
  Button,
  Chip,
  Sheet,
  Switch,
} from '@mui/joy';
import {
  Ban,
  Archive,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Ambulance,
  Helicopter,
  Backpack,
  BriefcaseMedical,
  Pencil,
} from 'lucide-react';
import ModaleMezzo from '../../components/ModaleMezzo';
import { authFetch } from '../../utils/authFetch';
import { canEditVehicles } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModaleConferma from '../../components/ModaleConferma';

export default function Mezzi() {
  const navigate = useNavigate();
  const selectedOrg = localStorage.getItem('selectedOrg');

  const [showModale, setShowModale] = useState(false);

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [page, setPage] = useState(1);
  const perPage = 20;

  const [sortConfig, setSortConfig] = useState({
    key: 'name_vehicle',
    direction: 'asc',
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  const typeLabels = {
    1: { label: 'Ambulanza', icon: <Ambulance size={16} /> , color: 'primary' },
    2: { label: 'Automedica', icon: <BriefcaseMedical size={16} />, color: 'danger' },
    3: { label: 'Autoinfermieristica', icon: <BriefcaseMedical size={16} />, color: 'warning' },
    4: { label: 'Eli', icon: <Helicopter size={16} />, color: 'danger' },
    5: { label: 'Zaino', icon: <Backpack size={16} />, color: 'primary' },
  };

  const fetchVehicles = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/all`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Errore fetch Risorse');

      const data = await res.json();
      setVehicles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore nel fetch risorse:', err);
      setVehicles([]);
      toast.error('Errore nel caricamento delle risorse', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('it-IT');
  };

  const normalize = (v) => (v ?? '').toString().toLowerCase();

  const filteredVehicles = useMemo(() => {
    const term = normalize(searchTerm);

    return vehicles
      .filter((v) => (showArchived ? true : v.status !== 2))
      .filter((v) => {
        if (!term) return true;
        return (
          normalize(v.name_vehicle).includes(term) ||
          normalize(v.code_vehicle).includes(term) ||
          normalize(v.category_name).includes(term) ||
          normalize(v.convention_name).includes(term)
        );
      });
  }, [vehicles, searchTerm, showArchived]);

  const sortedVehicles = useMemo(() => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    const getComparable = (v) => {
      const val = v?.[key];

      // numeri
      if (key === 'id' || key === 'category_id' || key === 'type_convention') {
        return Number(val) || 0;
      }

      // date
      if (key === 'last_check') {
        const t = val ? new Date(val).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      }

      // stringhe
      return normalize(val);
    };

    return [...filteredVehicles].sort((a, b) => {
      const A = getComparable(a);
      const B = getComparable(b);
      if (A < B) return -1 * dir;
      if (A > B) return 1 * dir;
      return 0;
    });
  }, [filteredVehicles, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedVehicles.length / perPage));

  // se cambia la lista (filtri/ricerca) e sei oltre il totale pagine, rientra
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedVehicles = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedVehicles.slice(start, start + perPage);
  }, [page, sortedVehicles]);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((cur) => {
      if (cur.key === key) {
        return { key, direction: cur.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowDown size={14} color="#bbb" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} color="black" />
      : <ArrowDown size={14} color="black" />;
  };

  const handleConfirmArchive = async () => {
    const vehicleId = vehicleToDelete?.id;
    if (!vehicleId) return;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/${vehicleId}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vehicle_id: vehicleId, status: 2 }),
        }
      );

      if (!res.ok) throw new Error('Errore nella modifica');

      toast.success('Mezzo archiviato', { position: 'top-right', autoClose: 2500 });
      fetchVehicles();
    } catch (err) {
      console.error(err);
      toast.error('Errore nella modifica del mezzo', { position: 'top-right', autoClose: 3000 });
    } finally {
      setConfirmDeleteOpen(false);
      setVehicleToDelete(null);
    }
  };

  const handleToggleStatus = async (vehicleId, vehicleStatus) => {
    const nuovoStatus = vehicleStatus === 1 ? 0 : 1;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/vehicle/${vehicleId}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vehicle_id: vehicleId, status: nuovoStatus }),
        }
      );

      if (!res.ok) throw new Error('Errore nella modifica');

      toast.success('Stato del mezzo aggiornato', { position: 'top-right', autoClose: 2500 });
      fetchVehicles();
    } catch (err) {
      console.error(err);
      toast.error('Errore nella modifica del mezzo', { position: 'top-right', autoClose: 3000 });
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Risorse
      </Typography>

      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 'lg',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Input
            placeholder="Cerca (nome, codice, categoria, convenzione)..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ width: { xs: '100%', sm: 380 } }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <Switch
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setPage(1);
              }}
            />
            <Typography level="body-sm">Mostra archiviati</Typography>
          </Box>

          <Typography level="body-sm" color="neutral">
            {sortedVehicles.length} record trovati
          </Typography>
        </Box>

        {canEditVehicles() && (
          <Button variant="solid" color="primary" onClick={() => setShowModale(true)}>
            + Aggiungi Risorsa
          </Button>
        )}
      </Sheet>

      {loading ? (
        <Typography>Caricamento...</Typography>
      ) : sortedVehicles.length === 0 ? (
        <Typography>Nessuna risorsa trovata.</Typography>
      ) : (
        <Box
          sx={{
            borderRadius: 'lg',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
          }}
        >
          <Table
            size="sm"
            sx={{
              minWidth: { xs: '100%', sm: 700 },
              tableLayout: 'auto',
              '& thead th': {
                bgcolor: 'neutral.softBg',
                textAlign: 'left',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'xs', sm: 'sm' },
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              },
              '& tbody td': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: 1.2,
                verticalAlign: 'middle',
              },
              '& tbody tr:hover': {
                bgcolor: 'neutral.plainHoverBg',
              },
            }}
          >
            <thead>
              <tr>
                <th onClick={() => handleSort('name_vehicle')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Nome <SortIcon columnKey="name_vehicle" />
                  </Box>
                </th>

                <th onClick={() => handleSort('type_vehicle')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Tipo <SortIcon columnKey="type_vehicle" />
                  </Box>
                </th>

                <th onClick={() => handleSort('category_name')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Categoria <SortIcon columnKey="category_name" />
                  </Box>
                </th>

                <th onClick={() => handleSort('convention_name')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Convenzione <SortIcon columnKey="convention_name" />
                  </Box>
                </th>

                <th onClick={() => handleSort('code_vehicle')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Codice <SortIcon columnKey="code_vehicle" />
                  </Box>
                </th>

                <th onClick={() => handleSort('last_check')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Ultimo controllo <SortIcon columnKey="last_check" />
                  </Box>
                </th>

                {canEditVehicles() && (
                  <th style={{ textAlign: 'center', width: 120, cursor: 'default' }}>
                    Azioni
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedVehicles.map((v) => {
                const type = typeLabels[Number(v.type_vehicle)];
                const isArchived = v.status === 2;

                const statusDot =
                  v.status === 1
                    ? 'success.solidBg'
                    : v.status === 0
                    ? 'danger.solidBg'
                    : 'neutral.outlinedDisabledColor';

                return (
                  <tr key={v.id}>
                    <td>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/risorse/${v.id}`)}
                        title="Apri dettaglio"
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusDot }} />
                        <Typography level="body-sm" fontWeight="lg">
                          {v.name_vehicle}
                        </Typography>
                      </Box>
                    </td>

                    <td>
                      {type ? (
                        <Chip
                          variant="soft"
                          color={type.color}
                          startDecorator={type.icon}
                          size="sm"
                        >
                          {type.label}
                        </Chip>
                      ) : (
                        <Typography level="body-sm" color="neutral">—</Typography>
                      )}
                    </td>

                    <td>{v.category_name || '—'}</td>
                    <td>{v.convention_name || '—'}</td>
                    <td>{v.code_vehicle || '—'}</td>
                    <td>{formatDate(v.last_check) || '—'}</td>

                    {canEditVehicles() && (
                      <td style={{ textAlign: 'center' }}>
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => navigate(`/risorse/${v.id}`)}
                          >
                            <Pencil size={16} />
                          </IconButton>

                          <IconButton
                            size="sm"
                            variant="soft"
                            color={v.status === 1 ? 'danger' : 'success'}
                            disabled={isArchived}
                            onClick={() => handleToggleStatus(v.id, v.status)}
                            title={isArchived ? 'Mezzo archiviato' : 'Attiva/Disattiva'}
                          >
                            <Ban size={16} />
                          </IconButton>

                          <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            disabled={isArchived}
                            onClick={() => {
                              setVehicleToDelete(v);
                              setConfirmDeleteOpen(true);
                            }}
                            title={isArchived ? 'Già archiviato' : 'Archivia'}
                          >
                            <Archive size={16} />
                          </IconButton>
                        </Box>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Box>
      )}

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="sm"
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            startDecorator={<ChevronLeft size={14} />}
          >
            Precedente
          </Button>

          <Typography level="body-sm">
            Pagina {page} di {totalPages}
          </Typography>

          <Button
            size="sm"
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            endDecorator={<ChevronRight size={14} />}
          >
            Successiva
          </Button>
        </Box>
      )}

      <ModaleMezzo
        open={showModale}
        onClose={() => setShowModale(false)}
        onSuccess={() => {
          fetchVehicles();
          setShowModale(false);
          toast.success('Mezzo aggiunto/modificato con successo', {
            position: 'top-right',
            autoClose: 3000,
          });
        }}
      />

      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmArchive}
        title="Conferma archiviazione"
        message={
          <>
            Sei sicuro di voler archiviare <b>{vehicleToDelete?.code_vehicle || 'questo mezzo'}</b>?
            <br />
            Non sarà più possibile ripristinarlo.
          </>
        }
      />
    </Box>
  );
}